import React, { FC, useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../api';
import './launches-dashboard.scss';

/**
 * Launches tab of Platform Stats — staged feature rollouts.
 *
 * A launch is a YAML brief in dream-be (ai-agents/launches/*.yaml) that targets
 * users by cached traits. Ops previews the audience (with a skip reason per
 * excluded user), opens a small wave, watches how recipients respond, then
 * opens the next wave or pauses. Recipients get one message from their own
 * assistant at a calendar-aware time (or right now for a test wave).
 *
 * Shapes mirror dream-be's lib/launches/apiTypes.ts until promoted to
 * @dreamai/types.
 */

interface LaunchSummary {
  slug: string;
  name: string;
  briefVersion: number;
  description: string;
  enableFlag: string | null;
  traits: { trait: string; min: number }[];
  imageUrl: string | null;
  imageCaption: string | null;
  /** 'en' plus every language the YAML localises the image/caption for */
  languages: string[];
  adoptionSignal: string;
  adoptionWindowDays: number;
  status: 'draft' | 'live' | 'paused' | 'closed';
  waves: number;
  recipients: number;
}

interface LaunchListResponse {
  success: boolean;
  launches: LaunchSummary[];
  ledgerKeyConfigured: boolean;
}

interface AudiencePreviewRow {
  userId: string;
  name: string | null;
  createdAt: string | null;
  language: string;
  traits: Record<string, number | null>;
  evidence: Record<string, string | null>;
  eligible: boolean;
  skipReason: string | null;
}

interface AudiencePreviewResponse {
  success: boolean;
  eligible: number;
  skipped: Record<string, number>;
  rows: AudiencePreviewRow[];
  refresh: { slug: string; candidates: number; recomputed: number }[];
  /** language the sample directive/image below were resolved for */
  language: string;
  sampleDirective: string;
  sampleImageUrl: string | null;
  sampleImageCaption: string | null;
}

interface OpenWaveResponse {
  success: boolean;
  wave: number;
  scheduled: number;
  skipped: number;
}

interface WaveStats {
  wave: number;
  briefVersion: number;
  total: number;
  scheduled: number;
  dispatched: number;
  sent: number;
  failed: number;
  skipped: number;
  outcomes: Record<string, number>;
  adopted: number;
}

interface LaunchRecipientRow {
  id: string;
  userId: string;
  name: string | null;
  wave: number;
  briefVersion: number;
  status: string;
  scheduledFor: string | null;
  scheduleReason: string | null;
  sentAt: string | null;
  skipReason: string | null;
  outcome: string;
  adoptedAt: string | null;
}

interface LaunchDashboardResponse {
  success: boolean;
  launch: LaunchSummary;
  waves: WaveStats[];
  recipients: LaunchRecipientRow[];
}

interface RefreshOutcomesResponse {
  success: boolean;
  classified: number;
  noReply: number;
  adopted: number;
}

const SKIP_LABELS: Record<string, string> = {
  trait_missing: 'Trait not computed yet',
  trait_below_min: 'Below trait threshold',
  too_new: 'Account too new',
  already_enabled: 'Already has the feature',
  already_in_launch: 'Already in this launch',
  recent_launch: 'Heard from another launch recently',
  opted_out: 'Asked not to hear about new features',
  no_slot: 'No free slot in the delivery window',
  launch_not_live: 'Launch was paused before delivery',
  user_active_too_long: 'User kept being active at every re-check',
  no_dispatcher_agent: 'No secretary configured',
  no_credits: 'Company has no credits',
  delivery_failed: 'The secretary never posted the announcement',
};

const STATUS_LABELS: Record<string, string> = {
  scheduled: 'scheduled',
  dispatched: 'sending…',
  sent: 'sent',
  failed: 'failed',
  skipped: 'skipped',
  cancelled: 'cancelled',
};

const SCHEDULE_LABELS: Record<string, string> = {
  after_meeting: 'after a meeting',
  preferred_hour: 'preferred hour',
  first_free_gap: 'first free gap',
  no_calendar: 'no calendar, preferred hour',
  immediate: 'sent now',
};

const OUTCOME_ORDER = [
  'positive',
  'curious',
  'neutral',
  'declined',
  'confused',
  'opted_out',
  'no_reply',
  'pending',
];

const fmtDate = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString('en-GB', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

const label = (map: Record<string, string>, key: string | null) =>
  key ? (map[key] ?? key.replace(/_/g, ' ')) : '—';

const LANGUAGE_NAMES: Record<string, string> = {
  en: 'English',
  fr: 'French',
  de: 'German',
  es: 'Spanish',
  it: 'Italian',
};
const languageLabel = (code: string) =>
  LANGUAGE_NAMES[code] ? `${LANGUAGE_NAMES[code]} (${code})` : code;

const LaunchesDashboard: FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [launches, setLaunches] = useState<LaunchSummary[] | null>(null);
  const [ledgerKeyConfigured, setLedgerKeyConfigured] = useState(true);
  const [selected, setSelected] = useState<string | null>(null);
  const [preview, setPreview] = useState<AudiencePreviewResponse | null>(null);
  const [dashboard, setDashboard] = useState<LaunchDashboardResponse | null>(
    null,
  );
  const [waveSize, setWaveSize] = useState(25);
  const [timing, setTiming] = useState<'calendar' | 'now'>('calendar');
  const [previewLanguage, setPreviewLanguage] = useState('en');
  const [showSkipped, setShowSkipped] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const authed = useCallback(async () => {
    const token = await getAccessTokenSilently();
    return { headers: { Authorization: `Bearer ${token}` } };
  }, [getAccessTokenSilently]);

  const fetchLaunches = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<LaunchListResponse>(
        '/launch-stats/launches',
        await authed(),
      );
      setLaunches(res.data.launches);
      setLedgerKeyConfigured(res.data.ledgerKeyConfigured);
      setSelected((current) => current ?? res.data.launches[0]?.slug ?? null);
    } catch {
      setError('Failed to load launches');
    } finally {
      setLoading(false);
    }
  }, [authed]);

  const fetchDashboard = useCallback(
    async (slug: string) => {
      const res = await api.get<LaunchDashboardResponse>(
        `/launch-stats/launches/${slug}`,
        await authed(),
      );
      setDashboard(res.data);
      setLaunches((current) =>
        current
          ? current.map((l) => (l.slug === slug ? res.data.launch : l))
          : current,
      );
    },
    [authed],
  );

  const fetchPreview = useCallback(
    async (
      slug: string,
      options: { language?: string; refresh?: boolean } = {},
    ) => {
      setBusy('preview');
      setError(null);
      try {
        const params = new URLSearchParams({
          language: options.language ?? 'en',
        });
        if (options.refresh === false) params.set('refresh', '0');
        const res = await api.get<AudiencePreviewResponse>(
          `/launch-stats/launches/${slug}/preview?${params}`,
          await authed(),
        );
        setPreview(res.data);
      } catch {
        setError('Failed to preview the audience');
      } finally {
        setBusy(null);
      }
    },
    [authed],
  );

  useEffect(() => {
    fetchLaunches();
  }, [fetchLaunches]);

  useEffect(() => {
    if (!selected) return;
    setPreview(null);
    setPreviewLanguage('en');
    setDashboard(null);
    setNotice(null);
    fetchDashboard(selected).catch(() =>
      setError('Failed to load launch status'),
    );
  }, [selected, fetchDashboard]);

  const openWave = async () => {
    if (!selected || !preview) return;
    setBusy('wave');
    setError(null);
    setNotice(null);
    try {
      const res = await api.post<OpenWaveResponse>(
        `/launch-stats/launches/${selected}/waves`,
        { size: waveSize, timing },
        await authed(),
      );
      setNotice(
        `Wave ${res.data.wave} opened: ${res.data.scheduled} scheduled${
          res.data.skipped ? `, ${res.data.skipped} skipped` : ''
        }${timing === 'now' ? ' — delivering now' : ''}.`,
      );
      await Promise.all([fetchDashboard(selected), fetchPreview(selected)]);
    } catch {
      setError('Failed to open the wave');
    } finally {
      setBusy(null);
    }
  };

  const setStatus = async (status: 'live' | 'paused' | 'closed') => {
    if (!selected) return;
    setBusy('status');
    setError(null);
    try {
      await api.post(
        `/launch-stats/launches/${selected}/status`,
        { status },
        await authed(),
      );
      await fetchDashboard(selected);
    } catch {
      setError('Failed to change launch status');
    } finally {
      setBusy(null);
    }
  };

  const refreshOutcomes = async () => {
    if (!selected) return;
    setBusy('outcomes');
    setError(null);
    try {
      const res = await api.post<RefreshOutcomesResponse>(
        `/launch-stats/launches/${selected}/outcomes`,
        {},
        await authed(),
      );
      setNotice(
        `Outcomes refreshed: ${res.data.classified} replies classified, ${res.data.noReply} no reply, ${res.data.adopted} adopted.`,
      );
      await fetchDashboard(selected);
    } catch {
      setError('Failed to refresh outcomes');
    } finally {
      setBusy(null);
    }
  };

  const launch =
    dashboard?.launch ?? launches?.find((l) => l.slug === selected) ?? null;
  const eligibleRows = preview?.rows.filter((r) => r.eligible) ?? [];
  const skippedRows = preview?.rows.filter((r) => !r.eligible) ?? [];
  const traitSlugs = launch?.traits.map((t) => t.trait) ?? [];

  return (
    <div className='launches-dashboard'>
      {error && <div className='msg error'>{error}</div>}
      {loading && !launches && <div className='loading'>Loading...</div>}

      {!ledgerKeyConfigured && (
        <div className='msg error'>
          LAUNCH_LEDGER_KEY is not set on the backend — trait computation and
          previews are disabled until it is.
        </div>
      )}

      {launches && launches.length === 0 && (
        <p className='launches-empty'>
          No launches defined. Add a YAML brief under ai-agents/launches in
          dream-be.
        </p>
      )}

      {launches && launches.length > 0 && (
        <div className='launches-layout'>
          <aside className='launches-list'>
            {launches.map((l) => (
              <button
                key={l.slug}
                className={`launch-item ${selected === l.slug ? 'active' : ''}`}
                onClick={() => setSelected(l.slug)}
              >
                <span className='launch-item-name'>{l.name}</span>
                <span className={`launch-status launch-status--${l.status}`}>
                  {l.status}
                </span>
                <span className='launch-item-meta'>
                  v{l.briefVersion} · {l.waves} wave{l.waves === 1 ? '' : 's'} ·{' '}
                  {l.recipients} recipient{l.recipients === 1 ? '' : 's'}
                </span>
              </button>
            ))}
          </aside>

          {launch && (
            <section className='launch-detail'>
              <header className='launch-header'>
                <div>
                  <h3>
                    {launch.name}{' '}
                    <span
                      className={`launch-status launch-status--${launch.status}`}
                    >
                      {launch.status}
                    </span>
                  </h3>
                  <p className='launch-description'>{launch.description}</p>
                  <p className='launch-rules'>
                    Audience:{' '}
                    {launch.traits
                      .map((t) => `${t.trait} ≥ ${t.min}`)
                      .join(', ')}
                    {launch.enableFlag && (
                      <>
                        {' '}
                        · switches on <code>{launch.enableFlag}</code> on
                        delivery
                      </>
                    )}{' '}
                    · adoption = {launch.adoptionSignal.replace(/_/g, ' ')}{' '}
                    within {launch.adoptionWindowDays}d
                  </p>
                </div>
                <div className='launch-status-actions'>
                  {launch.status === 'live' && (
                    <button
                      className='launch-btn'
                      disabled={busy !== null}
                      onClick={() => setStatus('paused')}
                    >
                      Pause
                    </button>
                  )}
                  {launch.status === 'paused' && (
                    <button
                      className='launch-btn'
                      disabled={busy !== null}
                      onClick={() => setStatus('live')}
                    >
                      Resume
                    </button>
                  )}
                  {launch.status !== 'closed' && launch.status !== 'draft' && (
                    <button
                      className='launch-btn launch-btn--quiet'
                      disabled={busy !== null}
                      onClick={() => setStatus('closed')}
                    >
                      Close
                    </button>
                  )}
                </div>
              </header>

              {notice && <div className='launch-notice'>{notice}</div>}

              {/* ── Audience ─────────────────────────────────────────── */}
              <div className='launch-card'>
                <div className='launch-card-header'>
                  <h4>Audience</h4>
                  <button
                    className='launch-btn'
                    disabled={busy !== null || !ledgerKeyConfigured}
                    onClick={() =>
                      fetchPreview(launch.slug, { language: previewLanguage })
                    }
                  >
                    {busy === 'preview'
                      ? 'Computing…'
                      : preview
                        ? 'Refresh preview'
                        : 'Preview audience'}
                  </button>
                </div>

                {!preview && busy !== 'preview' && (
                  <p className='launch-hint'>
                    Preview computes any missing or stale traits for the
                    audience rule, then lists who would get this launch and why
                    everyone else is skipped. Nothing is sent.
                  </p>
                )}

                {preview && (
                  <>
                    <div className='launch-summary-row'>
                      <div className='launch-stat'>
                        <span className='launch-stat-value'>
                          {preview.eligible}
                        </span>
                        <span className='launch-stat-label'>eligible now</span>
                      </div>
                      {Object.entries(preview.skipped).map(([reason, n]) => (
                        <div
                          className='launch-stat launch-stat--muted'
                          key={reason}
                        >
                          <span className='launch-stat-value'>{n}</span>
                          <span className='launch-stat-label'>
                            {label(SKIP_LABELS, reason)}
                          </span>
                        </div>
                      ))}
                      {preview.refresh.map((r) => (
                        <div
                          className='launch-stat launch-stat--muted'
                          key={r.slug}
                        >
                          <span className='launch-stat-value'>
                            {r.recomputed}/{r.candidates}
                          </span>
                          <span className='launch-stat-label'>
                            {r.slug} recomputed (rest cached)
                          </span>
                        </div>
                      ))}
                    </div>

                    <table className='launch-table'>
                      <thead>
                        <tr>
                          <th>User</th>
                          <th>Lang</th>
                          {traitSlugs.map((t) => (
                            <th key={t}>{t}</th>
                          ))}
                          <th>Why</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {eligibleRows.map((row) => (
                          <tr key={row.userId}>
                            <td className='user-name'>
                              {row.name ?? row.userId}
                            </td>
                            <td className='launch-lang'>{row.language}</td>
                            {traitSlugs.map((t) => (
                              <td key={t} className='launch-score'>
                                {row.traits[t] == null
                                  ? '—'
                                  : row.traits[t]!.toFixed(2)}
                              </td>
                            ))}
                            <td className='launch-evidence'>
                              {traitSlugs
                                .map((t) => row.evidence[t])
                                .filter(Boolean)
                                .join(' · ') || '—'}
                            </td>
                            <td>
                              <span className='launch-pill launch-pill--ok'>
                                eligible
                              </span>
                            </td>
                          </tr>
                        ))}
                        {showSkipped &&
                          skippedRows.map((row) => (
                            <tr
                              key={row.userId}
                              className='launch-row--skipped'
                            >
                              <td className='user-name'>
                                {row.name ?? row.userId}
                              </td>
                              <td className='launch-lang'>{row.language}</td>
                              {traitSlugs.map((t) => (
                                <td key={t} className='launch-score'>
                                  {row.traits[t] == null
                                    ? '—'
                                    : row.traits[t]!.toFixed(2)}
                                </td>
                              ))}
                              <td className='launch-evidence'>
                                {traitSlugs
                                  .map((t) => row.evidence[t])
                                  .filter(Boolean)
                                  .join(' · ') || '—'}
                              </td>
                              <td>
                                <span className='launch-pill'>
                                  {label(SKIP_LABELS, row.skipReason)}
                                </span>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                    {skippedRows.length > 0 && (
                      <button
                        className='launch-link'
                        onClick={() => setShowSkipped((v) => !v)}
                      >
                        {showSkipped
                          ? 'Hide skipped users'
                          : `Show ${skippedRows.length} skipped user${
                              skippedRows.length === 1 ? '' : 's'
                            }`}
                      </button>
                    )}

                    <div className='launch-wave-controls'>
                      <label>
                        Wave size
                        <input
                          type='number'
                          min={1}
                          max={500}
                          value={waveSize}
                          onChange={(e) =>
                            setWaveSize(
                              Math.max(1, Number(e.target.value) || 1),
                            )
                          }
                        />
                      </label>
                      <label>
                        Timing
                        <select
                          value={timing}
                          onChange={(e) =>
                            setTiming(e.target.value as 'calendar' | 'now')
                          }
                        >
                          <option value='calendar'>
                            Best slot from each user's calendar
                          </option>
                          <option value='now'>Send now (test wave)</option>
                        </select>
                      </label>
                      <button
                        className='launch-btn launch-btn--primary'
                        disabled={
                          busy !== null ||
                          preview.eligible === 0 ||
                          launch.status === 'closed'
                        }
                        onClick={openWave}
                      >
                        {busy === 'wave'
                          ? 'Opening…'
                          : `Open wave ${(launch.waves ?? 0) + 1} (${Math.min(
                              waveSize,
                              preview.eligible,
                            )} users)`}
                      </button>
                    </div>
                  </>
                )}
              </div>

              {/* ── Message ──────────────────────────────────────────── */}
              <div className='launch-card'>
                <div className='launch-card-header'>
                  <h4>What recipients get</h4>
                  <span className='launch-card-sub'>
                    brief v{launch.briefVersion} — their secretary writes the
                    message in its own voice, in the user's language
                  </span>
                  {launch.languages.length > 1 && (
                    <label className='launch-language-picker'>
                      Preview as
                      <select
                        value={previewLanguage}
                        disabled={busy !== null}
                        onChange={(e) => {
                          const language = e.target.value;
                          setPreviewLanguage(language);
                          if (preview) {
                            fetchPreview(launch.slug, {
                              language,
                              refresh: false,
                            });
                          }
                        }}
                      >
                        {launch.languages.map((code) => (
                          <option key={code} value={code}>
                            {languageLabel(code)}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                </div>
                {(() => {
                  const imageUrl = preview
                    ? preview.sampleImageUrl
                    : launch.imageUrl;
                  const imageCaption = preview
                    ? preview.sampleImageCaption
                    : launch.imageCaption;
                  return (
                    <div className='launch-message'>
                      {imageUrl && (
                        <figure className='launch-image'>
                          <img
                            src={imageUrl}
                            alt={imageCaption ?? launch.name}
                          />
                          {imageCaption && (
                            <figcaption>{imageCaption}</figcaption>
                          )}
                        </figure>
                      )}
                      {preview ? (
                        <details className='launch-directive'>
                          <summary>
                            Directive the secretary receives (
                            {languageLabel(preview.language)})
                          </summary>
                          <pre>{preview.sampleDirective}</pre>
                        </details>
                      ) : (
                        <p className='launch-hint'>
                          Preview the audience to see the exact directive.
                        </p>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* ── Waves ────────────────────────────────────────────── */}
              <div className='launch-card'>
                <div className='launch-card-header'>
                  <h4>Waves</h4>
                  {dashboard && dashboard.waves.length > 0 && (
                    <button
                      className='launch-btn'
                      disabled={busy !== null}
                      onClick={refreshOutcomes}
                    >
                      {busy === 'outcomes'
                        ? 'Classifying…'
                        : 'Refresh outcomes'}
                    </button>
                  )}
                </div>
                {!dashboard && <div className='loading'>Loading...</div>}
                {dashboard && dashboard.waves.length === 0 && (
                  <p className='launch-hint'>
                    No waves yet. Preview the audience above, then open a small
                    first wave.
                  </p>
                )}
                {dashboard && dashboard.waves.length > 0 && (
                  <table className='launch-table'>
                    <thead>
                      <tr>
                        <th>Wave</th>
                        <th>Brief</th>
                        <th>Scheduled</th>
                        <th>Sending</th>
                        <th>Sent</th>
                        <th>Failed</th>
                        <th>Skipped</th>
                        {OUTCOME_ORDER.map((o) => (
                          <th key={o}>{o.replace(/_/g, ' ')}</th>
                        ))}
                        <th>Adopted</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboard.waves.map((w) => (
                        <tr key={w.wave}>
                          <td className='user-name'>Wave {w.wave}</td>
                          <td>v{w.briefVersion}</td>
                          <td>{w.scheduled}</td>
                          <td>{w.dispatched}</td>
                          <td>{w.sent}</td>
                          <td>{w.failed}</td>
                          <td>{w.skipped}</td>
                          {OUTCOME_ORDER.map((o) => (
                            <td key={o} className='launch-score'>
                              {w.outcomes[o] ?? 0}
                            </td>
                          ))}
                          <td>
                            {w.adopted}
                            {w.sent > 0 && (
                              <span className='launch-muted'>
                                {' '}
                                ({Math.round((w.adopted / w.sent) * 100)}%)
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* ── Recipients ───────────────────────────────────────── */}
              {dashboard && dashboard.recipients.length > 0 && (
                <div className='launch-card'>
                  <div className='launch-card-header'>
                    <h4>Recipients</h4>
                    <span className='launch-card-sub'>
                      one-line coarse summary only — never the reply itself
                    </span>
                  </div>
                  <table className='launch-table'>
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Wave</th>
                        <th>Status</th>
                        <th>When</th>
                        <th>Outcome</th>
                        <th>Adopted</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboard.recipients.map((r) => (
                        <tr key={r.id}>
                          <td className='user-name'>{r.name ?? r.userId}</td>
                          <td>{r.wave}</td>
                          <td>
                            <span
                              className={`launch-pill ${
                                r.status === 'sent'
                                  ? 'launch-pill--ok'
                                  : r.status === 'skipped' ||
                                      r.status === 'failed'
                                    ? 'launch-pill--warn'
                                    : ''
                              }`}
                            >
                              {r.status === 'skipped' || r.status === 'failed'
                                ? label(SKIP_LABELS, r.skipReason)
                                : label(STATUS_LABELS, r.status)}
                            </span>
                          </td>
                          <td>
                            {r.status === 'sent' || r.status === 'dispatched'
                              ? fmtDate(r.sentAt)
                              : fmtDate(r.scheduledFor)}
                            {r.scheduleReason && (
                              <span className='launch-muted'>
                                {' '}
                                · {label(SCHEDULE_LABELS, r.scheduleReason)}
                              </span>
                            )}
                          </td>
                          <td>{r.outcome.replace(/_/g, ' ')}</td>
                          <td>{r.adoptedAt ? fmtDate(r.adoptedAt) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}
        </div>
      )}
    </div>
  );
};

export default LaunchesDashboard;
